/**
 * The programmes a plot can be in, and what each one asks for (owner, 2026-09-27: "Display scope
 * with Dropdown: FSC FM, FSC CW (OVS), EUDR, Global GAP etc.", "scope tab fields for each selected
 * gets displayed based on selection", "Add Carbon Project also?").
 *
 * `ops.plot_scopes` holds one row per plot per scope with a shared set of columns; this says which
 * of them each scope uses and what they are called there. FSC FM uses the plot's own CB approval
 * and FSC claim fields instead, because the harvest claim rules read them on the plot.
 */
export type ScopeColumn = 'approved_on' | 'reference_no' | 'certificate_no' | 'valid_to' | 'assessment' | 'project_name' | 'notes'
  | 'excision_declaration' | 'excised_area_ha' | 'excision_reason' | 'excision_description';

export interface ScopeField {
  column: ScopeColumn;
  label: string;
  kind: 'date' | 'text' | 'choice' | 'number' | 'textarea';
  hint?: string;
  choices?: Array<{ id: string; label: string }>;
  required?: boolean;
  /** Asked only when the scope's other answers call for it. */
  showIf?: (values: Partial<Record<ScopeColumn, string>>) => boolean;
}

/**
 * The excision declaration (owner, 2026-09-27: "we have a declaration called 'Excision Areas' (not
 * required documents, but require declaration)"): part of a certified member's land left out of the
 * certified area, whose produce can never carry the claim. Migration 20260927550000.
 */
export const EXCISION_REASONS = [
  { id: 'conservation_set_aside', label: 'Conservation set-aside' }, { id: 'hcv_area', label: 'High conservation value area' },
  { id: 'other_land_use', label: 'Under another land use (crops, house, ...)' }, { id: 'dispute_or_encroachment', label: 'In dispute or encroached' },
  { id: 'converted_after_cutoff', label: 'Converted after the cut-off date' }, { id: 'not_under_management', label: 'Not under the member\'s management' },
  { id: 'other', label: 'Other' },
];
const excised = (values: Partial<Record<ScopeColumn, string>>) => values.excision_declaration === 'excised';
export const EXCISION_FIELDS: ScopeField[] = [
  { column: 'excision_declaration', label: 'Excision areas (declaration)', kind: 'choice', required: true,
    hint: 'Declared by the farmer; no document is needed. Produce from an excised area can never carry the certificate\'s claim.',
    choices: [{ id: 'none', label: 'No part of this plot is excised' }, { id: 'excised', label: 'Part of this plot is excised from the certified area' }] },
  { column: 'excised_area_ha', label: 'Excised area (ha)', kind: 'number', required: true, showIf: excised },
  { column: 'excision_reason', label: 'Reason', kind: 'choice', required: true, choices: EXCISION_REASONS, showIf: excised },
  { column: 'excision_description', label: 'Where it is, and anything else', kind: 'textarea', showIf: excised,
    hint: 'e.g. the 0.3 ha by the stream on the north side, kept as a buffer.' },
];

export interface ScopeDefinition {
  code: string;
  label: string;
  /** FSC FM: shown with the plot's own CB approval and FSC claim fields. */
  usesPlotFields?: boolean;
  fields: ScopeField[];
}

const CERTIFICATE_FIELDS = (numberLabel: string): ScopeField[] => [
  { column: 'certificate_no', label: numberLabel, kind: 'text', required: true },
  { column: 'approved_on', label: 'Certified on', kind: 'date' },
  { column: 'valid_to', label: 'Valid to', kind: 'date' },
];

export const PLOT_SCOPES: ScopeDefinition[] = [
  { code: 'FSC_FM', label: 'FSC FM (forest management)', usesPlotFields: true, fields: EXCISION_FIELDS },
  {
    code: 'FSC_CW', label: 'FSC CW (OVS)',
    fields: [
      { column: 'approved_on', label: 'Verified on', kind: 'date' },
      { column: 'reference_no', label: 'OVS verification reference', kind: 'text', hint: 'The verification report or supplier list reference.' },
      {
        column: 'assessment', label: 'Risk designation', kind: 'choice',
        choices: [{ id: 'low', label: 'Low risk' }, { id: 'specified', label: 'Specified risk' }, { id: 'unspecified', label: 'Unspecified risk' }],
      },
    ],
  },
  {
    code: 'EUDR', label: 'EUDR',
    fields: [
      {
        column: 'assessment', label: 'Deforestation-free since 31 Dec 2020', kind: 'choice', required: true,
        choices: [{ id: 'yes', label: 'Yes - no deforestation or degradation' }, { id: 'no', label: 'No' }, { id: 'pending', label: 'Not yet assessed' }],
      },
      { column: 'approved_on', label: 'Assessed on', kind: 'date' },
      { column: 'reference_no', label: 'Due diligence statement (DDS) reference', kind: 'text' },
    ],
  },
  { code: 'GLOBALGAP', label: 'GLOBALG.A.P.', fields: CERTIFICATE_FIELDS('GGN (GLOBALG.A.P. Number)') },
  { code: 'PEFC', label: 'PEFC', fields: [...CERTIFICATE_FIELDS('PEFC certificate number'), ...EXCISION_FIELDS] },
  { code: 'ORGANIC', label: 'Organic (NPOP / NOP)', fields: CERTIFICATE_FIELDS('Organic certificate number') },
  {
    code: 'CARBON', label: 'Carbon project',
    fields: [
      { column: 'project_name', label: 'Project name', kind: 'text', required: true },
      {
        column: 'assessment', label: 'Standard', kind: 'choice',
        choices: [
          { id: 'verra_vcs', label: 'Verra (VCS)' }, { id: 'gold_standard', label: 'Gold Standard' },
          { id: 'plan_vivo', label: 'Plan Vivo' }, { id: 'ccts', label: 'Indian carbon market (CCTS)' }, { id: 'other', label: 'Other' },
        ],
      },
      { column: 'certificate_no', label: 'Registry project ID', kind: 'text' },
      { column: 'approved_on', label: 'Enrolled on', kind: 'date' },
      { column: 'valid_to', label: 'Crediting period ends', kind: 'date' },
    ],
  },
];

export function scopeLabel(code: string): string {
  return PLOT_SCOPES.find((scope) => scope.code === code)?.label ?? code;
}
